import React, { useEffect, useRef, useState } from "react";
import { IconButton, Switch, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useDispatch } from "react-redux";
import { pushAlert } from "../../../../../utils/alertSlice";
import { addUpdateGroupMember, fetchManagePermissionGroup } from "../../../../../utils/api/managePermissionApi";
import Loader from "../../../../UniversalComponents/Loader";

const groupPermissionsByCategory = (permissions, isEnabled) => {
  const grouped = {};

  permissions
    .filter((perm) => perm.is_enabled === isEnabled)
    .forEach((perm) => {
      if (!grouped[perm.category]) grouped[perm.category] = [];
      grouped[perm.category].push(perm);
    });

  return Object.entries(grouped).map(([category, items]) => ({
    category,
    items,
  }));
};

const EditPermissionPanel = ({ onClose, groupData, setRefetch }) => {
  const [activeTab, setActiveTab] = useState("enabled");
  const [isLoading, setIsloading] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const drawerRef = useRef(null);

  useEffect(() => {
    drawerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [groupData]);

  const dispatch = useDispatch();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handlePermissionToggle = async (item) => {
    try {
      setPermissions((prev) =>
        prev.map((perm) => (perm.id === item?.id ? { ...perm, is_enabled: perm.is_enabled ? 0 : 1 } : perm))
      );

      const response = await addUpdateGroupMember({
        body: {
          group_id: groupData?.group_id,
          modules: [
            {
              module_id: item?.id,
              module_permission: item?.is_enabled ? 0 : 1,
            },
          ],
        },
      });

      if (response?.success) {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "success" }));
        setRefetch((prev) => !prev);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    } finally {
      getPermissionList();
    }
  };

  const currentPermissions = groupPermissionsByCategory(permissions, activeTab === "enabled" ? 1 : 0);

  const getPermissionList = async () => {
    try {
      setIsloading(true);
      const response = await fetchManagePermissionGroup({
        query: { key: "all_permission", group_id: groupData?.group_id },
      });
      if (response?.success) {
        setPermissions(response?.data || []);
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    } finally {
      setIsloading(false);
    }
  };

  useEffect(() => {
    getPermissionList();
  }, [groupData]);

  return (
    <div className="flex flex-col border-[1px] border-gray-200 h-full w-full">
      <div className="p-4 flex flex-col h-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-4" ref={drawerRef}>
          <h2 className="text-[16px] font-medium text-[#1E293B]">
            Edit Permission <span className="text-gray-500 font-normal">({groupData?.group_name})</span>
          </h2>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Tabs */}
        <div className="flex items-center border-b border-gray-200 mb-3">
          <button
            className={`text-sm font-medium px-3 py-2 border-b-2 ${
              activeTab === "enabled" ? "text-[#2563EB] border-[#2563EB]" : "text-gray-400 border-transparent"
            }`}
            onClick={() => handleTabChange("enabled")}
          >
            Enabled Permissions
          </button>
          <button
            className={`text-sm font-medium px-3 py-2 border-b-2 ${
              activeTab === "disabled" ? "text-[#2563EB] border-[#2563EB]" : "text-gray-400 border-transparent"
            }`}
            onClick={() => handleTabChange("disabled")}
          >
            Disabled Permissions
          </button>
        </div>

        {/* Permissions List */}
        <div className="flex flex-col gap-5 overflow-y-auto pr-1">
          {currentPermissions.length === 0 && (
            <p className="text-gray-500 text-sm text-center">No permissions found.</p>
          )}

          {currentPermissions.map((section, index) => (
            <div key={index}>
              <div className="flex items-center mb-2">
                <span className="text-sm font-medium text-gray-700">{section.category}</span>
                {section.items.length > 0 && (
                  <Tooltip title="More info">
                    <InfoOutlinedIcon className="ml-1 text-gray-400" fontSize="small" />
                  </Tooltip>
                )}
              </div>

              <div className="flex flex-col gap-2">
                {section.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between bg-[#F9FAFB] border border-gray-200 rounded-md px-3 py-2"
                  >
                    <span className="text-sm text-gray-800">{item.name}</span>
                    <Switch
                      size="small"
                      checked={item.is_enabled === 1}
                      onChange={() => handlePermissionToggle(item)}
                      disabled={groupData?.is_locked === 1}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <Loader isOpen={isLoading} />
    </div>
  );
};

export default EditPermissionPanel;
